import {InputElement} from "./input-element.js";

/**
 * Represents a radio button input element within a form.
 * This class extends the InputElement class to specifically handle
 * functionality and behavior for radio button components.
 */
export class RadioElement extends InputElement {
    text;
    value;

    /**
     * Creates an instance of the class with the given form data, parent element, and collection.
     *
     * @param {Object} formData - The data to be used for the form.
     * @param {HTMLElement} parentElement - The parent DOM element where the instance will be attached.
     * @param {array} collection - A collection associated with the instance.
     * @return {void}
     */
    constructor(formData, parentElement, collection) {
        super(formData, parentElement, collection);
    }

    /**
     * Creates a label element, sets its content to the text property of the current object,
     * and appends it to the specified parent element.
     *
     * @return {HTMLElement} The created label element appended to the parent element.
     */
    componentWrapper() {
        const labelElement = document.createElement('label');
        labelElement.appendChild(document.createTextNode(this.text));
        this.parentElement.appendChild(labelElement);

        return labelElement;
    }

    /**
     * Adds a component to the beginning of the specified wrapper.
     *
     * @param {Element} wrapper - The parent element to which the component will be added.
     * @param {Element} component - The element representing the component to be added.
     * @return {void} Does not return a value.
     */
    addComponent(wrapper, component) {
        wrapper.prepend(component);
    }

    /**
     * Sets the label for a specified element or component.
     * This method updates the existing label or assigns a new one to be displayed.
     *
     * @param {string} label - The text or identifier to be used as a label.
     * @throws {Error} Throws an error if the provided label is not a string or is empty.
     */
    setLabel() {}

    /**
     * Returns the input type for the method.
     *
     * @return {string} The input type value, which is "radio".
     */
    inputType() {
        return "radio";
    }

    /**
     * Extends the base component with additional properties.
     * Adds specific customization to the component by setting its `value`
     * and `selected` state based on the current instance values.
     *
     * @return {Object} The modified component with additional properties.
     */
    component() {
        const component = super.component();
        component.value = this.value;

        component.selected = this.value === this.dataValue;
        return component;
    }
}
